import { useEffect, useState } from "react"
import { useMutation } from "@apollo/client"
import { useTranslation } from "react-i18next"
import { Loader2, MessageSquareOff } from "lucide-react"
import { Button } from "@/components/ui/button"
import { cn } from "@/lib/utils"
import { useAuth } from "@/lib/auth"
import { ConversationView, FIND_OR_CREATE_LESSON_CONVERSATION } from "@/components/chat"
import type { ChatUser, ChatViewer } from "@/components/chat"
import { useClassroomDoc } from "./LiveDocProvider"

interface ChatPanelProps {
  className?: string
}

interface LessonConversation {
  id: string
  muted: boolean
  tutorCanCapture: boolean
  otherParticipant: ChatUser | null
}

// The chat panel unmounts every time the user toggles chat closed. Cache the
// resolved conversation per lesson (module scope) so reopening is instant and
// doesn't re-run find-or-create or re-show the spinner. find-or-create is
// idempotent, so the value is stable for the session.
const conversationCache = new Map<string, LessonConversation>()
const inFlight = new Set<string>()

// The in-classroom chat: the REAL (student, tutor) messaging thread, not a mock.
// Resolves the lesson's conversation once when opened, then mounts the shared
// ConversationView (history, send, typing, read receipts — all already built).
export function ChatPanel({ className }: ChatPanelProps) {
  const { t } = useTranslation()
  const { user } = useAuth()
  const { lessonId } = useClassroomDoc()
  const [open, { loading }] = useMutation(FIND_OR_CREATE_LESSON_CONVERSATION)
  // Key the module-scope cache by viewer too: otherParticipant/muted are
  // resolved relative to context[:current_user], so a second user logging into
  // the same tab must not inherit the first user's cached conversation.
  const cacheKey = user && lessonId ? `${user.id}:${lessonId}` : null
  const [conversation, setConversation] = useState<LessonConversation | null>(() =>
    cacheKey ? conversationCache.get(cacheKey) ?? null : null,
  )
  const [failed, setFailed] = useState(false)
  const [retryNonce, setRetryNonce] = useState(0)

  useEffect(() => {
    if (!lessonId || !cacheKey) return
    const cached = conversationCache.get(cacheKey)
    if (cached) {
      setConversation(cached)
      return
    }
    if (inFlight.has(cacheKey)) return
    inFlight.add(cacheKey)
    open({ variables: { input: { lessonId } } })
      .then((res) => {
        const payload = res.data?.findOrCreateLessonConversation
        const convo = payload?.conversation as LessonConversation | null | undefined
        if (!convo || (payload?.errors?.length ?? 0) > 0) {
          setFailed(true)
          return
        }
        conversationCache.set(cacheKey, convo)
        setConversation(convo)
      })
      .catch(() => setFailed(true))
      .finally(() => inFlight.delete(cacheKey))
  }, [lessonId, cacheKey, open, retryNonce])

  const centered = (children: React.ReactNode) => (
    <div className={cn("flex flex-col h-full items-center justify-center gap-2 p-4 text-center", className)}>
      {children}
    </div>
  )

  if (!user || !lessonId) {
    return centered(<p className="text-sm text-muted-foreground">{t("liveSession.chatUnavailable")}</p>)
  }
  if (failed) {
    return centered(
      <>
        <MessageSquareOff className="w-6 h-6 text-muted-foreground" />
        <p className="text-sm text-muted-foreground">{t("liveSession.chatLoadFailed")}</p>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => {
            setFailed(false)
            setRetryNonce((n) => n + 1)
          }}
        >
          {t("liveSession.tryAgain")}
        </Button>
      </>,
    )
  }
  if (loading || !conversation) {
    return centered(
      <>
        <Loader2 className="w-5 h-5 animate-spin text-primary" />
        <p className="text-sm text-muted-foreground">{t("liveSession.chatLoading")}</p>
      </>,
    )
  }

  const currentUser: ChatViewer = {
    id: user.id,
    firstName: user.firstName ?? "",
    lastName: user.lastName ?? "",
    initials: user.initials ?? "??",
    // Load-bearing, not decoration: the corrected-capture toast offers its
    // action to the two participants and words itself for the tutor.
    role: user.role,
  }

  return (
    <div className={cn("h-full", className)}>
      <ConversationView
        conversationId={conversation.id}
        currentUser={currentUser}
        otherParticipant={conversation.otherParticipant}
        muted={conversation.muted}
        tutorCanCapture={conversation.tutorCanCapture}
      />
    </div>
  )
}
